import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { staffHomePath } from "@/lib/staffAccess";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Accesso effettuato");
      const demoMail = String(u.email || "").toLowerCase();
      const isDemoUser = !!u.is_demo || demoMail === "demo@veltasistemi.it" || demoMail === "iovaneli.private@gmail.com";
      if (u.role === "superadmin" && !isDemoUser) nav("/admin");
      else if (u.role === "reseller") nav("/rivenditore");
      else if (u.role === "client_staff") nav(staffHomePath(u));
      else nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err, "Credenziali non valide"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#F7F7F8] text-[#3F3F46]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 420px at 12% -10%, rgba(183,60,39,0.06), transparent 55%), radial-gradient(700px 380px at 100% 110%, rgba(24,24,27,0.04), transparent 50%)",
        }}
      />

      <div className="relative z-10 flex min-h-screen flex-col px-6 py-8 sm:px-10 lg:px-16">
        <header className="mx-auto flex w-full max-w-6xl items-center justify-between">
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-2 text-sm text-[#71717A] transition-colors hover:text-[#27272A]"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna al sito
          </Link>
          <Link
            to="/preventivo"
            className="text-sm font-medium text-[#B73C27] transition-colors hover:text-[#98301F]"
          >
            Richiedi preventivo
          </Link>
        </header>

        <main className="mx-auto flex w-full max-w-6xl flex-1 items-center justify-center py-12 lg:py-16">
          <div className="grid w-full items-center gap-16 lg:grid-cols-[1.05fr_0.95fr]">
            <section className="hidden max-w-xl lg:block">
              <img
                src="/velta-one-mark.png"
                alt="Velta One"
                className="mb-10 h-14 w-24 object-contain object-left"
                data-testid="login-logo-mark"
              />
              <p className="mb-5 text-[11px] font-medium uppercase tracking-[0.28em] text-[#A1A1AA]">
                Accesso gestionale
              </p>
              <h1 className="font-heading text-5xl font-semibold leading-[1.08] tracking-tight text-[#18181B] xl:text-[3.25rem]">
                Il tuo negozio,
                <br />
                in tempo reale.
              </h1>
              <p className="mt-6 max-w-md text-base leading-relaxed text-[#52525B]">
                Statistiche, articoli, magazzino e chiusure fiscali in un unico portale, pensato per il lavoro di tutti i giorni.
              </p>
              <p className="mt-12 text-xs tracking-wide text-[#A1A1AA]">
                Velta One · by VELTA SISTEMI
              </p>
            </section>

            <section className="w-full">
              <div
                className="rounded-2xl border border-[#E4E4E7]/80 bg-white p-8 sm:p-10"
                style={{
                  boxShadow:
                    "8px 12px 28px rgba(24, 24, 27, 0.06), -6px -8px 20px rgba(255, 255, 255, 0.85), inset 0 1px 0 rgba(255,255,255,0.9)",
                }}
              >
                <div className="mb-8 flex items-center gap-3">
                  <img
                    src="/velta-one-mark.png"
                    alt="Velta One"
                    className="h-9 w-16 object-contain"
                    data-testid="login-logo"
                  />
                  <div className="font-heading text-lg font-semibold tracking-tight text-[#18181B]">
                    Velta One
                  </div>
                </div>

                <h2 className="font-heading text-3xl font-semibold tracking-tight text-[#18181B] sm:text-[2rem]">
                  Bentornato
                </h2>
                <p className="mt-2 mb-10 text-[15px] leading-relaxed text-[#71717A]">
                  Inserisci le credenziali del tuo account Velta One.
                </p>

                <form onSubmit={submit} className="space-y-6">
                  <div>
                    <label htmlFor="login-email" className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-[#71717A]">
                      Email
                    </label>
                    <input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-14 w-full rounded-xl border border-[#E4E4E7] bg-[#FAFAFA] px-4 text-base text-[#18181B] outline-none transition-[border-color,box-shadow,background-color] placeholder:text-[#A1A1AA] focus:border-[#B73C27]/40 focus:bg-white focus:ring-4 focus:ring-[#B73C27]/10"
                      style={{ boxShadow: "inset 0 1px 2px rgba(24,24,27,0.04)" }}
                      placeholder="nome@azienda.it"
                    />
                  </div>

                  <div>
                    <label htmlFor="login-password" className="mb-2 block text-[11px] font-medium uppercase tracking-[0.18em] text-[#71717A]">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="login-password"
                        data-testid="login-password-input"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-14 w-full rounded-xl border border-[#E4E4E7] bg-[#FAFAFA] px-4 pr-12 text-base text-[#18181B] outline-none transition-[border-color,box-shadow,background-color] placeholder:text-[#A1A1AA] focus:border-[#B73C27]/40 focus:bg-white focus:ring-4 focus:ring-[#B73C27]/10"
                        style={{ boxShadow: "inset 0 1px 2px rgba(24,24,27,0.04)" }}
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-[#A1A1AA] transition-colors hover:text-[#52525B]"
                        aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <button
                    data-testid="login-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="btn-tactile mt-2 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#B73C27] text-base font-medium text-white transition-colors hover:bg-[#98301F] disabled:opacity-60"
                    style={{ boxShadow: "0 10px 22px rgba(183, 60, 39, 0.22)" }}
                  >
                    {loading ? "Accesso in corso…" : <><LogIn className="h-5 w-5" /> Accedi</>}
                  </button>
                </form>

                <div className="mt-10 border-t border-[#E4E4E7] pt-8 text-sm text-[#71717A]">
                  Non hai ancora un account?{" "}
                  <Link
                    to="/preventivo"
                    data-testid="login-activate-link"
                    className="font-medium text-[#B73C27] transition-colors hover:text-[#98301F]"
                  >
                    Richiedi un preventivo
                  </Link>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
